"use client";
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination";
import { IBlog, IBlogPost, ITheme, IUserData } from "@/types";
import React, { useState } from "react";
import PostList from "./PostList";
import { BlogPostSkeletonBlock } from "./skeleton";
import { cn, shadow } from "@/lib/utils";
import { IconLoader2 } from "@tabler/icons-react";
import { SHADOW_OPTIONS } from "@/components/render/blocks/utils/block-style-classes";

interface PaginationProps {
    blog: IBlog;
    account: IUserData;
    totalItems: number;
    itemsPerPage: number;
    posts: IBlogPost[];
    categories?: string[] | null;
    pageNumber: number;
    accountTheme: ITheme;
    url?: string;
    v2Theme?: IUserData["chosen_theme"]["blog"];
}

const PaginateBlog: React.FC<PaginationProps> = ({
    blog,
    account,
    totalItems,
    itemsPerPage,
    posts,
    categories,
    pageNumber,
    accountTheme,
    url,
    v2Theme,
}) => {
    const [currentPage, setCurrentPage] = useState(pageNumber);
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const [Posts, setPosts] = useState(posts);
    React.useEffect(() => {
        setPosts(posts);
        setCurrentPage(pageNumber);
    }, [posts, pageNumber]);
    const [isLoading, setIsLoading] = useState(false);
    const handlePageChange = async (page: number) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
        setIsLoading(true);
        const res = await fetch(
            `/api/account/${account.id}/blog?page=${page}&limit=${itemsPerPage}${categories && categories?.length > 0 ? `&categories=[${categories}]` : ""}`
        );
        const data = await res.json();
        setPosts(data.data.posts);
        setIsLoading(false);
    };

    return (
        <>
            {isLoading && <BlogPostSkeletonBlock account={account} />}
            {isLoading && <BlogPostSkeletonBlock account={account} />}
            {isLoading && <BlogPostSkeletonBlock account={account} />}
            {!isLoading &&
                Posts &&
                Posts.map((post, index) => {
                    post.category_name =
                        blog.categories.find((e) => e.id == post.category)
                            ?.name || "";
                    return (
                        <PostList
                            key={index}
                            blog={blog}
                            assets={[]}
                            post={post}
                            account={account}
                            accountTheme={accountTheme}
                            url={url}
                            v2Theme={v2Theme}
                        />
                    );
                })}
            {totalPages > 1 && (
                <Pagination>
                    <PaginationContent
                        className={`w-fit gap-4 rounded-xl p-2`}
                        // style={{
                        //     backgroundColor:
                        //         // accountTheme.accent_col ||
                        //         getTextColorForBackground(
                        //             rgbToHex(accountTheme.button)
                        //         ),
                        // }}
                    >
                        <PaginationItem>
                            <PaginationPrevious
                                className={cn(
                                    `rounded-xl transition-all duration-100 hover:scale-110 ${shadow(accountTheme.button_shadow)} cursor-pointer`,
                                    v2Theme &&
                                        SHADOW_OPTIONS[v2Theme.design.shadow]
                                            ?.class
                                )}
                                style={{
                                    color: v2Theme
                                        ? v2Theme.colors.primary_text
                                        : accountTheme.text_col_button,
                                    background: v2Theme
                                        ? v2Theme.colors.primary
                                        : accountTheme.button,
                                    border: v2Theme
                                        ? `2px solid ${v2Theme.colors.tertiary}`
                                        : "none",
                                }}
                                onClick={() =>
                                    handlePageChange(currentPage - 1)
                                }
                            />
                        </PaginationItem>
                        {Array.from({ length: totalPages }, (_, i) => (
                            <PaginationItem key={i}>
                                <PaginationLink
                                    onClick={() => handlePageChange(i + 1)}
                                    className={cn(
                                        `aspect-square rounded-xl border-0 transition-all duration-100 hover:scale-110 ${shadow(accountTheme.button_shadow)} cursor-pointer px-3`,
                                        v2Theme &&
                                            SHADOW_OPTIONS[
                                                v2Theme.design.shadow
                                            ]?.class
                                    )}
                                    style={{
                                        color: v2Theme
                                            ? currentPage === i + 1
                                                ? v2Theme.colors.primary_text ||
                                                  v2Theme.colors.primary_text
                                                : v2Theme.colors.primary_text
                                            : currentPage === i + 1
                                              ? accountTheme.text_col_button ||
                                                accountTheme.text_col_button
                                              : accountTheme.text_col_button,
                                        background: v2Theme
                                            ? currentPage === i + 1
                                                ? v2Theme.colors.primary
                                                : "transparent"
                                            : currentPage === i + 1
                                              ? accountTheme.button
                                              : "transparent",
                                        border: v2Theme
                                            ? `2px solid ${v2Theme.colors.tertiary}`
                                            : "none",
                                    }}
                                >
                                    {i + 1}
                                </PaginationLink>
                            </PaginationItem>
                        ))}

                        <PaginationItem>
                            <PaginationNext
                                onClick={() =>
                                    handlePageChange(currentPage + 1)
                                }
                                className={cn(
                                    `rounded-xl transition-all duration-100 hover:scale-110 ${shadow(accountTheme.button_shadow)} cursor-pointer`,
                                    v2Theme &&
                                        SHADOW_OPTIONS[v2Theme.design.shadow]
                                            ?.class
                                )}
                                style={{
                                    color: v2Theme
                                        ? v2Theme.colors.primary_text
                                        : accountTheme.text_col_button,
                                    background: v2Theme
                                        ? v2Theme.colors.primary
                                        : accountTheme.button,
                                    border: v2Theme
                                        ? `2px solid ${v2Theme.colors.tertiary}`
                                        : "none",
                                }}
                            />
                        </PaginationItem>
                    </PaginationContent>
                </Pagination>
            )}
        </>
    );
};

export default PaginateBlog;

export const PaginateBlogCategory: React.FC<PaginationProps> = ({
    blog,
    account,
    totalItems,
    itemsPerPage,
    posts,
    categories,
    pageNumber,
    accountTheme,
    url,
    v2Theme,
}) => {
    const [currentPage, setCurrentPage] = useState(pageNumber);
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const [Posts, setPosts] = useState(posts);
    React.useEffect(() => {
        setPosts(posts);
        setCurrentPage(pageNumber);
    }, [posts, pageNumber]);
    const [isLoading, setIsLoading] = useState(false);
    const handlePageChange = async (page: number) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
        setIsLoading(true);
        const res = await fetch(
            `/api/account/${account.id}/blog?page=${page}&limit=${itemsPerPage}${categories && categories?.length > 0 ? `&categories=[${categories}]` : ""}`
        );
        const data = await res.json();
        setPosts(data.data.posts);
        setIsLoading(false);
    };

    return (
        <div className="space-y-12">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-6">
                {isLoading && (
                    <div className="col-span-full flex h-full w-full place-content-center items-center">
                        <IconLoader2
                            size={40}
                            className="animate-spin"
                            style={{
                                color: v2Theme
                                    ? v2Theme.colors.heading_text
                                    : accountTheme.text_col_heading ||
                                      "#000000",
                            }}
                        />
                    </div>
                )}
                {!isLoading &&
                    Posts &&
                    Posts.map((post, index) => {
                        post.category_name =
                            blog.categories.find((e) => e.id == post.category)
                                ?.name || "";
                        return (
                            <PostList
                                categoryView={true}
                                key={index}
                                blog={blog}
                                assets={[]}
                                post={post}
                                account={account}
                                accountTheme={accountTheme}
                                url={url}
                                isFirst={index === 0}
                                second={index === 1}
                                third={index === 2}
                                fourth={index === 3}
                                fifth={index === 4}
                                sixth={index === 5}
                                v2Theme={v2Theme}
                            />
                        );
                    })}
            </div>
            {totalPages > 1 && (
                <Pagination>
                    <PaginationContent className={`z-50 w-fit gap-4 p-2`}>
                        <PaginationItem>
                            <PaginationPrevious
                                className={cn(
                                    `rounded-xl transition-all duration-100 hover:scale-110 ${shadow(accountTheme.button_shadow)} cursor-pointer`,
                                    v2Theme &&
                                        SHADOW_OPTIONS[v2Theme.design.shadow]
                                            ?.class
                                )}
                                style={{
                                    color: v2Theme
                                        ? v2Theme.colors.primary_text
                                        : accountTheme.text_col_button,
                                    background: v2Theme
                                        ? v2Theme.colors.primary
                                        : accountTheme.button,
                                    border: v2Theme
                                        ? `2px solid ${v2Theme.colors.tertiary}`
                                        : "none",
                                }}
                                onClick={() =>
                                    handlePageChange(currentPage - 1)
                                }
                            />
                        </PaginationItem>
                        {Array.from({ length: totalPages }, (_, i) => (
                            <PaginationItem key={i}>
                                <PaginationLink
                                    onClick={() => handlePageChange(i + 1)}
                                    className={cn(
                                        `aspect-square rounded-xl border-0 transition-all duration-100 hover:scale-110 ${shadow(accountTheme.button_shadow)} cursor-pointer px-3`,
                                        v2Theme &&
                                            SHADOW_OPTIONS[
                                                v2Theme.design.shadow
                                            ]?.class
                                    )}
                                    style={{
                                        color: v2Theme
                                            ? currentPage === i + 1
                                                ? v2Theme.colors.primary_text ||
                                                  v2Theme.colors.primary_text
                                                : v2Theme.colors.primary_text
                                            : currentPage === i + 1
                                              ? accountTheme.text_col_button ||
                                                accountTheme.text_col_button
                                              : accountTheme.text_col_button,
                                        background: v2Theme
                                            ? currentPage === i + 1
                                                ? v2Theme.colors.primary
                                                : "transparent"
                                            : currentPage === i + 1
                                              ? accountTheme.button
                                              : "transparent",
                                        border: v2Theme
                                            ? `2px solid ${v2Theme.colors.tertiary}`
                                            : "none",
                                    }}
                                >
                                    {i + 1}
                                </PaginationLink>
                            </PaginationItem>
                        ))}

                        <PaginationItem>
                            <PaginationNext
                                onClick={() =>
                                    handlePageChange(currentPage + 1)
                                }
                                className={cn(
                                    `rounded-xl transition-all duration-100 hover:scale-110 ${shadow(accountTheme.button_shadow)} cursor-pointer`,
                                    v2Theme &&
                                        SHADOW_OPTIONS[v2Theme.design.shadow]
                                            ?.class
                                )}
                                style={{
                                    color: v2Theme
                                        ? v2Theme.colors.primary_text
                                        : accountTheme.text_col_button,
                                    background: v2Theme
                                        ? v2Theme.colors.primary
                                        : accountTheme.button,
                                    border: v2Theme
                                        ? `2px solid ${v2Theme.colors.tertiary}`
                                        : "none",
                                }}
                            />
                        </PaginationItem>
                    </PaginationContent>
                </Pagination>
            )}
        </div>
    );
};

export const PaginateBlogCategoryList: React.FC<PaginationProps> = ({
    blog,
    account,
    totalItems,
    itemsPerPage,
    posts,
    categories,
    pageNumber,
    accountTheme,
    url,
    v2Theme,
}) => {
    const [currentPage, setCurrentPage] = useState(pageNumber);
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const [Posts, setPosts] = useState(posts);
    React.useEffect(() => {
        setPosts(posts);
        setCurrentPage(pageNumber);
    }, [posts, pageNumber]);
    const [isLoading, setIsLoading] = useState(false);
    const handlePageChange = async (page: number) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
        setIsLoading(true);
        const res = await fetch(
            `/api/account/${account.id}/blog?page=${page}&limit=${itemsPerPage}${categories && categories?.length > 0 ? `&categories=[${categories}]` : ""}`
        );
        const data = await res.json();
        setPosts(data.data.posts);
        setIsLoading(false);
    };

    return (
        <div className="space-y-12">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-6">
                {isLoading && (
                    <div className="col-span-full flex h-full w-full place-content-center items-center">
                        <IconLoader2
                            size={40}
                            className="animate-spin"
                            style={{
                                color: v2Theme
                                    ? v2Theme.colors.heading_text
                                    : accountTheme.text_col_heading ||
                                      "#000000",
                            }}
                        />
                    </div>
                )}
                {!isLoading &&
                    Posts &&
                    Posts.map((post, index) => {
                        post.category_name =
                            blog.categories.find((e) => e.id == post.category)
                                ?.name || "";
                        return (
                            <PostList
                                categoryView={true}
                                key={index}
                                blog={blog}
                                assets={[]}
                                post={post}
                                account={account}
                                accountTheme={accountTheme}
                                url={url}
                                isFirst={index === 0}
                                second={index === 1}
                                third={index === 2}
                                fourth={index === 3}
                                fifth={index === 4}
                                sixth={index === 5}
                                v2Theme={v2Theme}
                                list={true}
                            />
                        );
                    })}
            </div>
            {totalPages > 1 && (
                <Pagination>
                    <PaginationContent className={`z-50 w-fit gap-4 p-2`}>
                        <PaginationItem>
                            <PaginationPrevious
                                className={cn(
                                    `rounded-xl transition-all duration-100 hover:scale-110 ${shadow(accountTheme.button_shadow)} cursor-pointer`,
                                    v2Theme &&
                                        SHADOW_OPTIONS[v2Theme.design.shadow]
                                            ?.class
                                )}
                                style={{
                                    color: v2Theme
                                        ? v2Theme.colors.primary_text
                                        : accountTheme.text_col_button,
                                    background: v2Theme
                                        ? v2Theme.colors.primary
                                        : accountTheme.button,
                                    border: v2Theme
                                        ? `2px solid ${v2Theme.colors.tertiary}`
                                        : "none",
                                }}
                                onClick={() =>
                                    handlePageChange(currentPage - 1)
                                }
                            />
                        </PaginationItem>
                        {Array.from({ length: totalPages }, (_, i) => (
                            <PaginationItem key={i}>
                                <PaginationLink
                                    onClick={() => handlePageChange(i + 1)}
                                    className={cn(
                                        `aspect-square rounded-xl border-0 transition-all duration-100 hover:scale-110 ${shadow(accountTheme.button_shadow)} cursor-pointer px-3`,
                                        v2Theme &&
                                            SHADOW_OPTIONS[
                                                v2Theme.design.shadow
                                            ]?.class
                                    )}
                                    style={{
                                        color: v2Theme
                                            ? currentPage === i + 1
                                                ? v2Theme.colors.primary_text ||
                                                  v2Theme.colors.primary_text
                                                : v2Theme.colors.primary_text
                                            : currentPage === i + 1
                                              ? accountTheme.text_col_button ||
                                                accountTheme.text_col_button
                                              : accountTheme.text_col_button,
                                        background: v2Theme
                                            ? currentPage === i + 1
                                                ? v2Theme.colors.primary
                                                : "transparent"
                                            : currentPage === i + 1
                                              ? accountTheme.button
                                              : "transparent",
                                        border: v2Theme
                                            ? `2px solid ${v2Theme.colors.tertiary}`
                                            : "none",
                                    }}
                                >
                                    {i + 1}
                                </PaginationLink>
                            </PaginationItem>
                        ))}

                        <PaginationItem>
                            <PaginationNext
                                onClick={() =>
                                    handlePageChange(currentPage + 1)
                                }
                                className={cn(
                                    `rounded-xl transition-all duration-100 hover:scale-110 ${shadow(accountTheme.button_shadow)} cursor-pointer`,
                                    v2Theme &&
                                        SHADOW_OPTIONS[v2Theme.design.shadow]
                                            ?.class
                                )}
                                style={{
                                    color: v2Theme
                                        ? v2Theme.colors.primary_text
                                        : accountTheme.text_col_button,
                                    background: v2Theme
                                        ? v2Theme.colors.primary
                                        : accountTheme.button,
                                    border: v2Theme
                                        ? `2px solid ${v2Theme.colors.tertiary}`
                                        : "none",
                                }}
                            />
                        </PaginationItem>
                    </PaginationContent>
                </Pagination>
            )}
        </div>
    );
};
